// Abfragen im Schwarzen Brett (Design-System §6 „Abfrage“): reine Logik ohne Netz,
// genutzt von den Functions (polls, admin-polls, poll-maintenance) und getestet in
// test/polls.test.ts.
import type { ValidationResult } from './validation';

export const MAX_POLL_TITLE = 120;
export const MAX_POLL_BODY = 2000;
export const MAX_OPTION_LABEL = 80;
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 12;
/** So lange nach Ende bleibt eine Abfrage im Schwarzen Brett sichtbar. */
export const SICHTBAR_NACH_ENDE_TAGE = 14;
/** Danach loescht poll-maintenance Abfrage samt Antworten. */
export const LOESCHEN_NACH_ENDE_TAGE = 90;
/** Erinnerungsmail so viele Tage vor Fristende (an alle ohne Antwort). */
export const ERINNERUNG_TAGE_VORHER = 2;
/** Uhrzeit (Berlin) der Erinnerungsmail. */
export const ERINNERUNG_STUNDE = 9;

export type ResultsVisibility = 'creator' | 'all_after_close' | 'all_live';
const VISIBILITIES: ResultsVisibility[] = ['creator', 'all_after_close', 'all_live'];

export interface PollInput {
  title: string;
  body_md: string;
  multiple: boolean;
  anonymous: boolean;
  results_visibility: ResultsVisibility;
  closes_at: string;
  options: string[];
}

export interface PollRow {
  id: string;
  title: string;
  body_md: string;
  multiple: boolean;
  anonymous: boolean;
  results_visibility: ResultsVisibility;
  closes_at: string;
  created_by: string | null;
}

export interface OptionRow {
  id: string;
  poll_id: string;
  label: string;
  position: number;
}

export function validatePoll(input: unknown, jetzt = new Date()): ValidationResult<PollInput> {
  if (typeof input !== 'object' || input === null) return { ok: false, error: 'Kein Objekt.' };
  const raw = input as Record<string, unknown>;

  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  if (!title) return { ok: false, error: 'Die Frage fehlt.' };
  if (title.length > MAX_POLL_TITLE) {
    return { ok: false, error: `Die Frage ist laenger als ${MAX_POLL_TITLE} Zeichen.` };
  }

  const body = typeof raw.body_md === 'string' ? raw.body_md.trim() : '';
  if (body.length > MAX_POLL_BODY) {
    return { ok: false, error: `Der Text ist laenger als ${MAX_POLL_BODY} Zeichen.` };
  }

  if (!Array.isArray(raw.options)) return { ok: false, error: 'Antwortoptionen fehlen.' };
  const options = raw.options
    .map((o) => (typeof o === 'string' ? o.trim() : ''))
    .filter((o) => o.length > 0);
  if (options.length < MIN_OPTIONS) {
    return { ok: false, error: `Mindestens ${MIN_OPTIONS} Antwortoptionen angeben.` };
  }
  if (options.length > MAX_OPTIONS) {
    return { ok: false, error: `Hoechstens ${MAX_OPTIONS} Antwortoptionen.` };
  }
  if (options.some((o) => o.length > MAX_OPTION_LABEL)) {
    return { ok: false, error: `Eine Option ist laenger als ${MAX_OPTION_LABEL} Zeichen.` };
  }
  if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) {
    return { ok: false, error: 'Zwei Antwortoptionen sind gleich.' };
  }

  if (typeof raw.closes_at !== 'string') return { ok: false, error: 'Die Frist fehlt.' };
  const closes = new Date(raw.closes_at);
  if (Number.isNaN(closes.getTime())) return { ok: false, error: 'Die Frist ist ungueltig.' };
  if (closes.getTime() <= jetzt.getTime() + 60 * 60 * 1000) {
    return { ok: false, error: 'Die Frist muss mindestens eine Stunde in der Zukunft liegen.' };
  }

  const visibility = raw.results_visibility ?? 'creator';
  if (!VISIBILITIES.includes(visibility as ResultsVisibility)) {
    return { ok: false, error: 'Unbekannte Sichtbarkeit.' };
  }

  return {
    ok: true,
    value: {
      title,
      body_md: body,
      multiple: raw.multiple === true,
      anonymous: raw.anonymous === true,
      results_visibility: visibility as ResultsVisibility,
      closes_at: closes.toISOString(),
      options,
    },
  };
}

export const istOffen = (poll: Pick<PollRow, 'closes_at'>, jetzt = new Date()) =>
  new Date(poll.closes_at).getTime() > jetzt.getTime();

/**
 * Darf diese Person die Zahlen sehen? Die anlegende Person immer, alle anderen
 * je nach Einstellung. Namen sieht ausser ihr niemand (nur im CSV-Export, und
 * nie bei anonymen Abfragen).
 */
export function ergebnisSichtbar(
  poll: Pick<PollRow, 'results_visibility' | 'closes_at' | 'created_by'>,
  userId: string,
  jetzt = new Date(),
): boolean {
  if (poll.created_by === userId) return true;
  if (poll.results_visibility === 'all_live') return true;
  return poll.results_visibility === 'all_after_close' && !istOffen(poll, jetzt);
}

/** Stimmen je Option, in der Reihenfolge der Optionen (nie nach Stimmen sortiert). */
export function zaehle(
  options: Pick<OptionRow, 'id' | 'position'>[],
  votes: { option_id: string }[],
): { option_id: string; count: number }[] {
  const n = new Map<string, number>();
  for (const v of votes) n.set(v.option_id, (n.get(v.option_id) ?? 0) + 1);
  return [...options]
    .sort((a, b) => a.position - b.position)
    .map((o) => ({ option_id: o.id, count: n.get(o.id) ?? 0 }));
}

const BERLIN = 'Europe/Berlin';

/** Datum (YYYY-MM-DD) und Stunde in Berlin. */
export function berlin(datum: Date): { tag: string; stunde: number } {
  const t = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: BERLIN,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(datum)
      .map((p) => [p.type, p.value]),
  );
  return { tag: `${t.year}-${t.month}-${t.day}`, stunde: Number(t.hour) };
}

/** Kalendertag in Berlin plus n Tage (sommerzeitfest ueber 12 Uhr UTC). */
function tagPlus(tag: string, n: number): string {
  const d = new Date(`${tag}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Netlify plant in UTC; poll-maintenance laeuft um 7 und 8 Uhr UTC. Erinnert
 * wird nur im Lauf, der in Berlin 9 Uhr ist – im Sommer wie im Winter.
 */
export const istErinnerungsZeit = (jetzt: Date) => berlin(jetzt).stunde === ERINNERUNG_STUNDE;

/** Endet die Abfrage (Berliner Kalender) in genau ERINNERUNG_TAGE_VORHER Tagen? */
export function erinnerungFaellig(poll: Pick<PollRow, 'closes_at'>, jetzt: Date): boolean {
  if (!istOffen(poll, jetzt)) return false;
  return berlin(new Date(poll.closes_at)).tag === tagPlus(berlin(jetzt).tag, ERINNERUNG_TAGE_VORHER);
}

/** „Fr, 14.11., 18:00 Uhr“ (Berliner Zeit) */
export function fristText(closesAt: string): string {
  const t = Object.fromEntries(
    new Intl.DateTimeFormat('de-DE', {
      timeZone: BERLIN,
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(closesAt))
      .map((p) => [p.type, p.value]),
  );
  return `${t.weekday?.replace('.', '')}, ${t.day}.${t.month}., ${t.hour}:${t.minute} Uhr`;
}

/** Text der Erinnerungsmail. Schlicht, ohne HTML, wie die Einladungsmail. */
export function erinnerungsText(
  name: string | null,
  poll: Pick<PollRow, 'title' | 'closes_at'>,
  link: string,
): { betreff: string; text: string } {
  const vorname = name?.trim().split(/\s+/)[0];
  return {
    betreff: `Abfrage endet bald: ${poll.title}`,
    text: `${vorname ? `Hallo ${vorname},` : 'Hallo,'}

im Hub läuft die Abfrage „${poll.title}“ noch bis ${fristText(poll.closes_at)}.
Du hast bisher nicht geantwortet – das geht mit einem Klick im Schwarzen Brett:

${link}

Diese Erinnerung kommt einmal, zwei Tage vor Ende.

Viele Grüße
SIRIUS document solutions`,
  };
}

/**
 * CSV fuer die anlegende Person (Name;Antwort), Semikolon wie in deutschem
 * Excel. Werte, die mit = + - @ beginnen, bekommen ein ' davor, damit Excel
 * sie nicht als Formel ausfuehrt (CSV-Injection).
 */
export function alsCsv(zeilen: { name: string; antwort: string }[]): string {
  const feld = (v: string) => {
    const sicher = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[";\n\r]/.test(sicher) ? `"${sicher.replace(/"/g, '""')}"` : sicher;
  };
  const kopf = 'Name;Antwort';
  const rumpf = [...zeilen]
    .sort((a, b) => a.name.localeCompare(b.name, 'de'))
    .map((z) => `${feld(z.name)};${feld(z.antwort)}`);
  // BOM, damit Excel UTF-8 (Umlaute) erkennt.
  return `﻿${[kopf, ...rumpf].join('\r\n')}\r\n`;
}

/** Fehlerschluessel aus poll_answer (Migration) → Meldung fuer die Oberflaeche. */
export const ANTWORT_FEHLER: Record<string, { status: number; text: string }> = {
  poll_not_found: { status: 404, text: 'Diese Abfrage gibt es nicht mehr.' },
  poll_closed: { status: 409, text: 'Die Abfrage ist beendet, Antworten sind nicht mehr moeglich.' },
  not_member: { status: 403, text: 'Fuer diese Abfrage fehlt die Berechtigung.' },
  no_option: { status: 400, text: 'Bitte eine Antwort auswaehlen.' },
  duplicate_option: { status: 400, text: 'Eine Antwort wurde doppelt gewaehlt.' },
  single_choice: { status: 400, text: 'Bei dieser Abfrage ist nur eine Antwort moeglich.' },
  foreign_option: { status: 400, text: 'Diese Antwort gehoert nicht zur Abfrage.' },
  already_answered: {
    status: 409,
    text: 'Du hast bei dieser anonymen Abfrage schon geantwortet; aendern ist nicht moeglich.',
  },
};

export function antwortFehler(message: string | undefined): { status: number; text: string } {
  const key = Object.keys(ANTWORT_FEHLER).find((k) => message?.includes(k));
  return key ? ANTWORT_FEHLER[key]! : { status: 500, text: 'Die Antwort konnte nicht gespeichert werden.' };
}

/** Eine Abfrage, wie /api/polls sie ausliefert (fuer alle; nie mit Namen). */
export interface PollView {
  id: string;
  title: string;
  body_md: string;
  multiple: boolean;
  anonymous: boolean;
  results_visibility: ResultsVisibility;
  closes_at: string;
  open: boolean;
  /** Von mir angelegt – dann sehe ich die Zahlen immer. */
  mine: boolean;
  options: { id: string; label: string }[];
  answered: boolean;
  /** Bei anonymen Abfragen immer leer. */
  my_option_ids: string[];
  /** null = Ergebnis fuer mich nicht freigegeben. */
  results: { option_id: string; count: number }[] | null;
  participants: number | null;
}

/** Ab zwei Tagen vor Ende „endet bald“ (Design-System §6). */
export function endetBald(closesAt: string, jetzt = new Date()): boolean {
  const rest = new Date(closesAt).getTime() - jetzt.getTime();
  return rest > 0 && rest <= ERINNERUNG_TAGE_VORHER * 86_400_000;
}

/** „Fr, 14.11. · 18:00“ fuer die Meta-Zeile der Karte. */
export function fristKurz(closesAt: string): string {
  return fristText(closesAt).replace(/\., (\d\d:\d\d) Uhr$/, '. · $1');
}

/** Offene unbeantwortete zuerst (nach Frist), dann offene beantwortete, dann beendete. */
export function sortiereFuerBrett(polls: PollView[]): PollView[] {
  const rang = (p: PollView) => (p.open ? (p.answered ? 1 : 0) : 2);
  return [...polls].sort(
    (a, b) =>
      rang(a) - rang(b) ||
      (rang(a) === 2
        ? b.closes_at.localeCompare(a.closes_at)
        : a.closes_at.localeCompare(b.closes_at)),
  );
}

export const SICHTBARKEIT_TEXT: Record<ResultsVisibility, string> = {
  creator: 'nur die anlegende person sieht die antworten',
  all_after_close: 'ergebnis für alle nach ende',
  all_live: 'ergebnis für alle sofort',
};
